import { useEffect, useState } from "react";

const API = process.env.REACT_APP_API_URL || "http://localhost:5001";

function token() {
  return localStorage.getItem("adminToken");
}

// ── Stat Card ─────────────────────────────────────────────────
function StatCard({ label, value, sub, color = "rose" }) {
  const colors = {
    rose:   "bg-rose-50 text-rose-600",
    blue:   "bg-blue-50 text-blue-600",
    green:  "bg-green-50 text-green-600",
    purple: "bg-purple-50 text-purple-600",
    orange: "bg-orange-50 text-orange-600",
    yellow: "bg-yellow-50 text-yellow-600",
  };
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
      <p className="text-xs text-gray-500 font-medium uppercase tracking-wide">{label}</p>
      <p className="text-2xl font-bold text-gray-900 mt-1">{value}</p>
      {sub && <p className={`text-xs mt-1 font-medium px-2 py-0.5 rounded-full inline-block ${colors[color]}`}>{sub}</p>}
    </div>
  );
}

// ── Status Badge ──────────────────────────────────────────────
function StatusBadge({ status }) {
  const map = {
    pending:    "bg-yellow-100 text-yellow-700",
    confirmed:  "bg-blue-100 text-blue-700",
    processing: "bg-purple-100 text-purple-700",
    shipped:    "bg-indigo-100 text-indigo-700",
    delivered:  "bg-green-100 text-green-700",
    cancelled:  "bg-red-100 text-red-700",
  };
  return (
    <span className={`text-xs font-semibold px-2.5 py-1 rounded-full capitalize ${map[status] || "bg-gray-100 text-gray-600"}`}>
      {status}
    </span>
  );
}

export default function Dashboard() {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState("");

  useEffect(() => {
  fetch(`${API}/api/admin/dashboard`, {
    headers: { Authorization: `Bearer ${token()}` },
  })
    .then(r => {
      if (!r.ok) throw new Error("Dashboard API error");
      return r.json();
    })
    .then(d => { setData(d); setLoading(false); })
    .catch((err) => { setError(err.message); setLoading(false); });
}, []);

  if (loading) return (
  <div className="flex items-center justify-center h-full min-h-screen">
    <div className="text-gray-400 text-sm animate-pulse">Loading dashboard...</div>
  </div>
);

if (error) return (
  <div className="p-8 text-rose-500 text-sm">{error}</div>
);

if (!data || !data.kpis) return (
  <div className="p-8 text-rose-500 text-sm">Dashboard data unavailable. Please refresh.</div>
);

const { kpis, order_status, charts, customers, recent_orders, recent_customers } = data;

  // Build order status map
  const statusMap = {};
(order_status || []).forEach(s => { statusMap[s.status] = s.count; });

  return (
    <div className="p-6 space-y-8 max-w-screen-xl mx-auto">

      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
        <p className="text-gray-500 text-sm mt-1">Welcome back! Here's what's happening today.</p>
      </div>

      {/* ── KPI Row 1 ── */}
      <div>
        <h2 className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-3">Overview</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard label="Total Orders"    value={kpis.total_orders}                            color="blue"   />
          <StatCard label="Total Revenue"   value={`$${Number(kpis.total_revenue).toLocaleString()}`}  color="green"  />
          <StatCard label="Total Products"  value={kpis.total_products}                          color="purple" />
          <StatCard label="Total Customers" value={kpis.total_customers}                         color="rose"   />
        </div>
      </div>

      {/* ── KPI Row 2 ── */}
      <div>
        <h2 className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-3">Today & This Month</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard label="Today's Orders"  value={kpis.today_orders}                                        color="blue"   />
          <StatCard label="Today's Revenue" value={`$${Number(kpis.today_revenue).toLocaleString()}`}         color="green"  />
          <StatCard label="Monthly Revenue" value={`$${Number(kpis.monthly_revenue).toLocaleString()}`}       color="purple" />
          <StatCard label="Avg Order Value" value={`$${Number(kpis.aov).toFixed(0)}`}                        color="orange" />
        </div>
      </div>

      {/* ── Inventory + Order Status ── */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

        {/* Inventory */}
        <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
          <h2 className="text-sm font-semibold text-gray-700 mb-4">📦 Inventory Health</h2>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Low Stock Products</span>
              <span className="text-sm font-bold text-yellow-600 bg-yellow-50 px-3 py-1 rounded-full">
                {kpis.low_stock}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Out of Stock</span>
              <span className="text-sm font-bold text-red-600 bg-red-50 px-3 py-1 rounded-full">
                {kpis.out_of_stock}
              </span>
            </div>
          </div>
        </div>

        {/* Order Status */}
        <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
          <h2 className="text-sm font-semibold text-gray-700 mb-4">🛒 Order Status Overview</h2>
          <div className="space-y-2">
            {["pending", "processing", "delivered", "cancelled"].map(s => (
              <div key={s} className="flex items-center justify-between">
                <span className="text-sm text-gray-600 capitalize">{s}</span>
                <StatusBadge status={s} />
                <span className="text-sm font-bold text-gray-800">{statusMap[s] || 0}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── Customer Insights ── */}
      <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
        <h2 className="text-sm font-semibold text-gray-700 mb-4">👥 Customer Insights</h2>
        <div className="grid grid-cols-2 gap-4">
          <StatCard label="New This Week"  value={customers.new_customers_week}  color="blue"  />
          <StatCard label="New This Month" value={customers.new_customers_month} color="green" />
        </div>
      </div>

      {/* ── Top Products ── */}
      <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
        <h2 className="text-sm font-semibold text-gray-700 mb-4">🏆 Top Selling Products</h2>
        {charts.topProducts.length === 0 ? (
          <p className="text-sm text-gray-400">No sales data yet.</p>
        ) : (
          <div className="space-y-3">
            {charts.topProducts.map((p, i) => (
              <div key={i} className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <span className="text-xs font-bold text-gray-400 w-5">#{i + 1}</span>
                  <span className="text-sm text-gray-800 font-medium">{p.name}</span>
                </div>
                <div className="text-right">
                  <p className="text-sm font-bold text-gray-900">{p.total_sold} sold</p>
                  <p className="text-xs text-gray-400">${Number(p.revenue).toLocaleString()}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── Revenue by Category ── */}
      <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
        <h2 className="text-sm font-semibold text-gray-700 mb-4">📈 Revenue by Category</h2>
        {charts.revenueByCategory.length === 0 ? (
          <p className="text-sm text-gray-400">No data yet.</p>
        ) : (
          <div className="space-y-3">
            {charts.revenueByCategory.map((c, i) => {
              const total = charts.revenueByCategory.reduce((a, b) => a + Number(b.revenue), 0);
              const pct   = total > 0 ? Math.round((Number(c.revenue) / total) * 100) : 0;
              return (
                <div key={i}>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-gray-700 font-medium">{c.category}</span>
                    <span className="text-gray-500">${Number(c.revenue).toLocaleString()} ({pct}%)</span>
                  </div>
                  <div className="w-full bg-gray-100 rounded-full h-2">
                    <div className="bg-rose-500 h-2 rounded-full transition-all" style={{ width: `${pct}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ── Recent Orders ── */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-100">
          <h2 className="text-sm font-semibold text-gray-700">🕐 Recent Orders</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-xs text-gray-500 uppercase">
              <tr>
                <th className="px-5 py-3 text-left">Order ID</th>
                <th className="px-5 py-3 text-left">Customer</th>
                <th className="px-5 py-3 text-left">Amount</th>
                <th className="px-5 py-3 text-left">Status</th>
                <th className="px-5 py-3 text-left">Date</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {recent_orders.map(o => (
                <tr key={o.id} className="hover:bg-gray-50 transition">
                  <td className="px-5 py-3 font-mono text-gray-600">#{o.id}</td>
                  <td className="px-5 py-3 text-gray-800">{o.customer_name}</td>
                  <td className="px-5 py-3 font-semibold text-gray-900">${Number(o.total).toLocaleString()}</td>
                  <td className="px-5 py-3"><StatusBadge status={o.status} /></td>
                  <td className="px-5 py-3 text-gray-400">{new Date(o.created_at).toLocaleDateString()}</td>
                </tr>
              ))}
              {recent_orders.length === 0 && (
                <tr><td colSpan={5} className="px-5 py-6 text-center text-gray-400">No orders yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── Recent Customers ── */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-100">
          <h2 className="text-sm font-semibold text-gray-700">👤 Recent Customers</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-xs text-gray-500 uppercase">
              <tr>
                <th className="px-5 py-3 text-left">Name</th>
                <th className="px-5 py-3 text-left">Email</th>
                <th className="px-5 py-3 text-left">Joined</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {recent_customers.map(c => (
                <tr key={c.id} className="hover:bg-gray-50 transition">
                  <td className="px-5 py-3 font-medium text-gray-800">{c.name}</td>
                  <td className="px-5 py-3 text-gray-500">{c.email}</td>
                  <td className="px-5 py-3 text-gray-400">{new Date(c.created_at).toLocaleDateString()}</td>
                </tr>
              ))}
              {recent_customers.length === 0 && (
                <tr><td colSpan={3} className="px-5 py-6 text-center text-gray-400">No customers yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

    </div>
  );
}